import React from 'react';
import moment from 'moment';
import { FilterMatchMode } from 'primereact/api';
import {
  dateRangeFilterTemplate,
  dateRangeFilterTemplateWithFuture,
  dateRangeFilterFunction,
  textFilterTemplate,
  multiSelectFilterTemplate,
} from '../../components/shared/DataTable/filterComponents';

const INVOICE_TYPE_OPTIONS = (t) => [
  { label: t('invoice_type.standard'), value: '10000' },
  { label: t('invoice_type.simplified'), value: '01000' },
  { label: t('invoice_type.standard_and_simplified'), value: '11000' },
];

const STATUS_OPTIONS = [
  { label: 'ACTIVE', value: 'ACTIVE' },
  { label: 'DEREGISTERED', value: 'REVOKED' },
  { label: 'EXPIRED', value: 'EXPIRED' },
];

export const ROUTES = [
  {
    label: 'view_list_of_devices',
    link: '',
  },
];

export const COLUMNS = ({ t }) => [
  {
    label: 'solutionList.common_name',
    value: 'commonName',
    sortable: false,
    filter: true,
    showSortInFilter: true,
    filterMatchMode: FilterMatchMode.CONTAINS,
    filterElement: (options) =>
      textFilterTemplate(
        options,
        t('search_by', { column: t('solutionList.common_name') }),
      ),
    headerTooltip: t('solutionList.common_name_tooltip'),
  },
  {
    label: 'solutionList.serial_number',
    body: (rowData) => rowData?.solutionSerialNumber || '-',
    value: 'solutionSerialNumber',
    sortable: false,
    filter: true,
    showSortInFilter: true,
    filterMatchMode: FilterMatchMode.CONTAINS,
    filterElement: (options) =>
      textFilterTemplate(
        options,
        t('search_by', { column: t('solutionList.serial_number') }),
      ),
    headerTooltip: t('solutionList.serial_number_tooltip'),
  },
  {
    label: 'solutionList.invoice_type',
    value: 'invoiceType',
    body: (rowData) => {
      return (
        {
          10000: t('invoice_type.standard'),
          '01000': t('invoice_type.simplified'),
          11000: t('invoice_type.standard_and_simplified'),
        }[rowData?.invoiceType] ?? rowData?.invoiceType
      );
    },
    sortable: false,
    filter: true,
    showSortInFilter: true,
    filterMatchMode: FilterMatchMode.CUSTOM,
    filterElement: (options) =>
      multiSelectFilterTemplate(
        { ...options, placeholder: t('solutionList.invoice_type') },
        INVOICE_TYPE_OPTIONS(t),
      ),
    filterFunction: (value, filter) => {
      if (!filter || filter.length === 0) {
        return true;
      }
      if (value === null || value === undefined) {
        return false;
      }
      return filter.includes(String(value));
    },
    filterValueFormatter: (value) => {
      if (Array.isArray(value)) {
        return value
          .map(
            (v) =>
              INVOICE_TYPE_OPTIONS(t).find(
                (opt) => String(opt.value) === String(v),
              )?.label || v,
          )
          .join(', ');
      }
      return (
        INVOICE_TYPE_OPTIONS(t).find(
          (opt) => String(opt.value) === String(value),
        )?.label || value
      );
    },
    headerTooltip: t('solutionList.invoice_type_tooltip'),
  },
  {
    label: 'solutionList.location',
    value: 'location',
    sortable: false,
    filter: true,
    showSortInFilter: true,
    filterMatchMode: FilterMatchMode.CONTAINS,
    filterElement: (options) =>
      textFilterTemplate(
        options,
        t('search_by', { column: t('solutionList.location') }),
      ),
    headerTooltip: t('solutionList.location_tooltip'),
  },
  {
    label: 'solutionList.country_name',
    value: 'countryName',
    sortable: false,
    filter: true,
    showSortInFilter: true,
    filterMatchMode: FilterMatchMode.CONTAINS,
    filterElement: (options) =>
      textFilterTemplate(
        options,
        t('search_by', { column: t('solutionList.country_name') }),
      ),
    headerTooltip: t('solutionList.country_name_tooltip'),
  },
  {
    label: 'solutionList.sub_cr',
    value: 'subCr',
    body: (rowData) => rowData?.subCr || '-',
    exportField: 'subCr',
    sortable: false,
    filter: true,
    showSortInFilter: true,
    filterMatchMode: FilterMatchMode.CONTAINS,
    filterElement: (options) =>
      textFilterTemplate(
        options,
        t('search_by', { column: t('solutionList.sub_cr') }),
      ),
    headerTooltip: t('solutionList.sub_cr_tooltip'),
  },
  {
    label: 'solutionList.onboarding_date',
    value: 'onBoardingDate',
    body: (rowData) =>
      rowData?.onBoardingDate && rowData?.onBoardingDate !== '-'
        ? moment(rowData.onBoardingDate, 'YYYY-MM-DD hh:mm:ss').format(
            'DD MMM YYYY',
          )
        : '-',
    sortable: false,
    filter: true,
    showSortInFilter: true,
    isDateColumn: true,
    filterMatchMode: FilterMatchMode.CUSTOM,
    filterElement: dateRangeFilterTemplate,
    filterFunction: dateRangeFilterFunction,
  },
  {
    label: 'solutionList.certificate_expiry_date',
    value: 'certifiedExpiryDate',
    body: (rowData) =>
      rowData?.certifiedExpiryDate && rowData?.certifiedExpiryDate !== '-'
        ? moment(rowData.certifiedExpiryDate, 'YYYY-MM-DD hh:mm:ss').format(
            'DD MMM YYYY',
          )
        : '-',
    sortable: false,
    filter: true,
    showSortInFilter: true,
    isDateColumn: true,
    filterMatchMode: FilterMatchMode.CUSTOM,
    filterElement: dateRangeFilterTemplateWithFuture,
    filterFunction: dateRangeFilterFunction,
  },
  {
    label: 'solutionList.status',
    value: 'status',
    body: (rowData) => {
      return (
        <div className="d-inline-flex align-items-center">
          {t(`solutionList.${rowData?.status}`)}
        </div>
      );
    },
    filter: true,
    showSortInFilter: true,
    filterMatchMode: FilterMatchMode.CUSTOM,
    filterElement: (options) =>
      multiSelectFilterTemplate(
        { ...options, placeholder: t('solutionList.status') },
        STATUS_OPTIONS.map((o) => ({
          ...o,
          label: t(`solutionList.${o.value}`),
        })),
      ),
    filterFunction: (value, filter) => {
      if (!filter || filter.length === 0) {
        return true;
      }
      if (value === null || value === undefined) {
        return false;
      }
      return filter.includes(String(value));
    },
    filterValueFormatter: (value) => {
      if (Array.isArray(value)) {
        return value.map((v) => t(`solutionList.${v}`)).join(', ');
      }
      return t(`solutionList.${value}`);
    },
  },
  {
    label: 'solutionList.revocation_date',
    value: 'revocationDate',
    body: (rowData) =>
      rowData?.revocationDate && rowData?.revocationDate !== '-'
        ? moment(rowData.revocationDate, 'YYYY-MM-DD hh:mm:ss').format(
            'DD MMM YYYY',
          )
        : '-',
    sortable: false,
    filter: true,
    showSortInFilter: true,
    isDateColumn: true,
    filterMatchMode: FilterMatchMode.CUSTOM,
    filterElement: dateRangeFilterTemplate,
    filterFunction: dateRangeFilterFunction,
    headerTooltip: t('solutionList.revocation_date_tooltip'),
  },
];

export const SORT_BY = COLUMNS({ t: (key) => key })
  .filter((column) => column.sortable)
  .map((column) => {
    return {
      value: column.value,
      label: column.label,
    };
  });

// PrimeReact popup column filters need initial filter models for each field.
export const SEARCH_CONFIG = {
  global: { value: null, matchMode: FilterMatchMode.CONTAINS },
  commonName: {
    operator: 'and',
    constraints: [{ value: null, matchMode: FilterMatchMode.CONTAINS }],
  },
  solutionSerialNumber: {
    operator: 'and',
    constraints: [{ value: null, matchMode: FilterMatchMode.CONTAINS }],
  },
  invoiceType: {
    operator: 'and',
    constraints: [{ value: null, matchMode: FilterMatchMode.CUSTOM }],
  },
  location: {
    operator: 'and',
    constraints: [{ value: null, matchMode: FilterMatchMode.CONTAINS }],
  },
  countryName: {
    operator: 'and',
    constraints: [{ value: null, matchMode: FilterMatchMode.CONTAINS }],
  },
  subCr: {
    operator: 'and',
    constraints: [{ value: null, matchMode: FilterMatchMode.CONTAINS }],
  },
  onBoardingDate: {
    operator: 'and',
    constraints: [{ value: null, matchMode: FilterMatchMode.CUSTOM }],
  },
  certifiedExpiryDate: {
    operator: 'and',
    constraints: [{ value: null, matchMode: FilterMatchMode.CUSTOM }],
  },
  status: {
    operator: 'and',
    constraints: [{ value: null, matchMode: FilterMatchMode.CUSTOM }],
  },
  revocationDate: {
    operator: 'and',
    constraints: [{ value: null, matchMode: FilterMatchMode.CUSTOM }],
  },
};
